import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, Modal, TouchableOpacity, ActivityIndicator, Platform, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { billingAPI, getBillingCheckoutConflict, getPaywallOfferDecline } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { colors, spacing } from '../../design-system';
import { Text } from '@/components/ui/text';
import { confirmDialog, showDialog } from '../../utils/dialogs';
import { trackAuthEvent, recordComebackOfferDeclined, recordFinalOfferDeclined } from '../../services/analytics';
import { checkoutRequestKey, createCheckoutRequestGate } from '../../utils/checkoutRequestGate';
import { isStaleChunkError, reloadIfNewerBuild } from '../../utils/staleBuild';
import {
  createCheckoutError,
  getCheckoutErrorDetails,
  markCheckoutErrorShown,
  reportCheckoutFailure,
  type CheckoutFailureFeedback,
} from '../../utils/checkoutFailures';

interface EmbeddedCheckoutModalProps {
  visible: boolean;
  planTier: 'tier1' | 'tier2' | 'tier3';
  billingCycle: '1mo' | '3mo' | '6mo';
  cardTrial?: boolean;
  trialPeriodDays?: number;
  /** Paywall-abandonment offer to attempt — step 1 "comeback" (30% off) or
   *  step 2 "final_offer" (fixed first-month price, paid). Silently dropped
   *  and retried without it if the backend declines eligibility. */
  promo?: 'comeback' | 'final_offer';
  onSessionCreated?: (session: { session_id?: string; currency?: string }) => void;
  onConflictResolved?: (result: { action: 'resume' | 'change_plan' }) => void | Promise<void>;
  onComplete: () => void | Promise<void>;
  onClose: () => void;
}

const DEFAULT_CARD_TRIAL_DAYS = 3;
const CHECKOUT_SURFACE = 'embedded_modal';

/**
 * Embedded Stripe Checkout modal — web only.
 * On native platforms, returns null (use openCheckoutSession utility instead).
 *
 * Loads @stripe/stripe-js + @stripe/react-stripe-js dynamically to avoid
 * bundling Stripe on native builds where it's not needed.
 */
export default function EmbeddedCheckoutModal({
  visible,
  planTier,
  billingCycle,
  cardTrial = false,
  trialPeriodDays = DEFAULT_CARD_TRIAL_DAYS,
  promo,
  onSessionCreated,
  onConflictResolved,
  onComplete,
  onClose,
}: EmbeddedCheckoutModalProps) {
  const isWeb = Platform.OS === 'web';
  const { t } = useTranslation();
  const { refreshAuth } = useAuth();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Parent callbacks are read through refs so a parent re-render (new
  // callback identity) can never re-trigger the session request below
  // (Oct 2026: re-fires 7-21 s after load swapped a loaded Stripe form for a
  // 409 error in 6 of 11 non-converting checkouts).
  const onSessionCreatedRef = useRef(onSessionCreated);
  onSessionCreatedRef.current = onSessionCreated;
  const onConflictResolvedRef = useRef(onConflictResolved);
  onConflictResolvedRef.current = onConflictResolved;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // One automatic request per open; stale responses never touch the UI and a
  // mounted Stripe form is never replaced (see utils/checkoutRequestGate).
  const gateRef = useRef(createCheckoutRequestGate());
  const completedRef = useRef(false);
  const requestKey = checkoutRequestKey({ planTier, billingCycle, cardTrial, trialPeriodDays, promo });
  // Remembers the last-shown conflict code so a later successful retry can
  // emit a matching `checkout_conflict_resolved` event (mirrors the pattern
  // already used for the resume/change_plan conflict branches below).
  const pendingConflictCodeRef = useRef<string | null>(null);
  // Flips true the moment the backend declines the comeback promo for this
  // checkout attempt, so the very next retry omits it and proceeds at full
  // price instead of looping or surfacing an error. A ref (not state) so the
  // in-flight retry inside fetchClientSecret's catch sees the update
  // immediately, without waiting on a re-render.
  const promoDeclinedRef = useRef(false);
  // Concurrent checkout-session requests are blocked by the gate: the retry
  // button plus the visibility effect could otherwise fire overlapping POSTs
  // (the Aug 31 incident logged 6 conflict 409s in 6 seconds).
  // Seconds the retry button stays disabled after an
  // intro_offer_checkout_in_progress conflict — driven by the backend's
  // retry_after_seconds, since retrying before the server-side reservation
  // lapses is guaranteed to 409 again.
  const [retryCooldown, setRetryCooldown] = useState(0);

  useEffect(() => {
    promoDeclinedRef.current = false;
  }, [visible, planTier, billingCycle, promo]);

  useEffect(() => {
    if (retryCooldown <= 0) return;
    const timer = setInterval(() => {
      setRetryCooldown((s) => (s > 1 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [retryCooldown > 0]);

  const showInlineCheckoutFailure = useCallback((checkoutError: unknown) => {
    const properties = {
      plan: planTier,
      billing_period: billingCycle,
    };
    const feedback = reportCheckoutFailure({
      error: checkoutError,
      surface: CHECKOUT_SURFACE,
      checkoutKey: `${planTier}:${billingCycle}`,
      properties,
      t,
    });
    markCheckoutErrorShown(feedback, CHECKOUT_SURFACE, properties);
    gateRef.current.setMountedSecret(null);
    setClientSecret(null);
    setError(feedback.message);
  }, [billingCycle, planTier, t]);

  const confirmBillingAction = useCallback((
    titleKey: string,
    messageKey: string,
    confirmKey: string,
  ): Promise<boolean> => {
    const title = t(titleKey);
    const message = t(messageKey);
    const confirmLabel = t(confirmKey);
    return confirmDialog(title, message, confirmLabel, t('billing.conflicts.dismissAction'));
  }, [t]);

  const requestCheckoutSession = useCallback(async (attempt: number) => {
    if (!billingAPI._stripePublishableKey) {
      await billingAPI.pricing();
    }
    if (!billingAPI._stripePublishableKey) {
      throw new Error('Stripe publishable key is unavailable');
    }
    const effectivePromo = promo && !promoDeclinedRef.current ? promo : undefined;
    // The final offer is explicitly pay-now / no-trial — never send
    // start_paid_trial alongside it, regardless of the cardTrial prop.
    const effectiveCardTrial = effectivePromo === 'final_offer' ? false : cardTrial;
    const result = await billingAPI.checkout(
      'b2c',
      planTier,
      billingCycle,
      undefined,
      'embedded',
      {
        startPaidTrial: effectiveCardTrial,
        trialPeriodDays: effectiveCardTrial ? trialPeriodDays : undefined,
        promo: effectivePromo,
      },
    );
    if (!result.client_secret) {
      throw createCheckoutError('missing_client_secret');
    }
    // Closed (or re-keyed) while in flight: the response is for a modal
    // instance the user has already left.
    if (!gateRef.current.isCurrent(attempt)) return;
    completedRef.current = false;
    gateRef.current.setMountedSecret(result.client_secret);
    setClientSecret(result.client_secret);
    onSessionCreatedRef.current?.({
      session_id: result.session_id,
      currency: result.currency,
    });
    if (pendingConflictCodeRef.current) {
      trackAuthEvent('checkout_conflict_resolved', {
        conflict_code: pendingConflictCodeRef.current,
        resolution: 'checkout_retry',
        surface: CHECKOUT_SURFACE,
      });
      pendingConflictCodeRef.current = null;
    }
  }, [billingCycle, cardTrial, planTier, trialPeriodDays, promo]);

  const handleCheckoutConflict = useCallback(async (
    err: any,
    retryCheckout: () => Promise<void>,
    initialFeedback?: CheckoutFailureFeedback,
  ): Promise<boolean> => {
    const conflict = err?.billingCheckoutConflict || getBillingCheckoutConflict(err);
    if (!conflict) return false;

    if (conflict.code === 'intro_offer_unavailable') {
      await billingAPI.refresh();
      await refreshAuth({ forceBillingRefresh: true });
      if (initialFeedback) {
        markCheckoutErrorShown(initialFeedback, CHECKOUT_SURFACE, {
          plan: planTier,
          billing_period: billingCycle,
        });
      }
      showDialog(
        t('billing.conflicts.introOfferUnavailableTitle'),
        t('billing.conflicts.introOfferUnavailableMessage'),
      );
      onCloseRef.current();
      return true;
    }
    if (conflict.code === 'intro_offer_checkout_in_progress') {
      if (initialFeedback) {
        markCheckoutErrorShown(initialFeedback, CHECKOUT_SURFACE, {
          plan: planTier,
          billing_period: billingCycle,
        });
      }
      pendingConflictCodeRef.current = conflict.code;
      trackAuthEvent('checkout_conflict_shown', {
        conflict_code: conflict.code,
        action: conflict.action || 'refresh',
        plan: planTier,
        billing_period: billingCycle,
        surface: CHECKOUT_SURFACE,
      });
      setError(t('billing.conflicts.introCheckoutInProgressMessage'));
      // Retrying before the server-side reservation lapses can only 409, so
      // hold the retry button until then (fall back to 15s if the backend
      // didn't say — older deploys omit retry_after_seconds).
      const retryAfter = Number(conflict.retry_after_seconds);
      setRetryCooldown(
        Number.isFinite(retryAfter) && retryAfter > 0 ? Math.min(retryAfter, 120) : 15,
      );
      return true;
    }

    const retryFreshCheckoutOnce = async (conflictCode: string): Promise<boolean> => {
      try {
        await retryCheckout();
        trackAuthEvent('checkout_conflict_resolved', {
          conflict_code: conflictCode,
          resolution: 'checkout_retry',
          surface: CHECKOUT_SURFACE,
        });
      } catch (retryError: any) {
        const retryConflict = getBillingCheckoutConflict(retryError);
        const failureConflictCode = retryConflict?.code || conflictCode;
        const diagnostics = getCheckoutErrorDetails(retryError);
        trackAuthEvent('checkout_conflict_resolution_failed', {
          conflict_code: failureConflictCode,
          resolution: 'checkout_retry',
          error_code: diagnostics.errorCode,
          error_type: diagnostics.errorType,
          surface: CHECKOUT_SURFACE,
        });
        showInlineCheckoutFailure(retryError);
      }
      return true;
    };

    if (conflict.action === 'checkout') {
      return retryFreshCheckoutOnce(conflict.code);
    }

    if (conflict.code === 'resume_available') {
      if (initialFeedback) {
        markCheckoutErrorShown(initialFeedback, CHECKOUT_SURFACE, {
          plan: planTier,
          billing_period: billingCycle,
        });
      }
      trackAuthEvent('checkout_conflict_shown', {
        conflict_code: conflict.code,
        action: conflict.action || 'resume',
        plan: planTier,
        billing_period: billingCycle,
        surface: CHECKOUT_SURFACE,
      });
      const confirmed = await confirmBillingAction(
        'billing.conflicts.resumeAvailableTitle',
        'billing.conflicts.resumeAvailableMessage',
        'billing.conflicts.resumeAction',
      );
      if (!confirmed) {
        trackAuthEvent('checkout_conflict_resolved', {
          conflict_code: conflict.code,
          resolution: 'dismissed',
          surface: CHECKOUT_SURFACE,
        });
        setError(t('billing.checkout.actionRequired'));
        return true;
      }
      try {
        await billingAPI.resumeSubscription();
        await billingAPI.refresh();
        await refreshAuth();
        await onConflictResolvedRef.current?.({ action: 'resume' });
        trackAuthEvent('checkout_conflict_resolved', {
          conflict_code: conflict.code,
          resolution: 'resume',
          surface: CHECKOUT_SURFACE,
        });
        onCloseRef.current();
      } catch (actionError: any) {
        const actionConflict = getBillingCheckoutConflict(actionError);
        if (actionConflict?.action === 'checkout') {
          return retryFreshCheckoutOnce(actionConflict.code);
        }
        const diagnostics = getCheckoutErrorDetails(actionError);
        trackAuthEvent('checkout_conflict_resolution_failed', {
          conflict_code: conflict.code,
          resolution: 'resume',
          error_code: diagnostics.errorCode,
          error_type: diagnostics.errorType,
          surface: CHECKOUT_SURFACE,
        });
        showInlineCheckoutFailure(actionError);
      }
      return true;
    }

    const titleKey = conflict.code === 'already_subscribed'
      ? 'billing.conflicts.alreadySubscribedTitle'
      : 'billing.conflicts.pendingCancellationTitle';
    const messageKey = conflict.code === 'already_subscribed'
      ? 'billing.conflicts.alreadySubscribedMessage'
      : 'billing.conflicts.pendingCancellationMessage';
    if (initialFeedback) {
      markCheckoutErrorShown(initialFeedback, CHECKOUT_SURFACE, {
        plan: planTier,
        billing_period: billingCycle,
      });
    }
    trackAuthEvent('checkout_conflict_shown', {
      conflict_code: conflict.code,
      action: conflict.action || 'change_plan',
      plan: planTier,
      billing_period: billingCycle,
      surface: CHECKOUT_SURFACE,
    });
    const confirmed = await confirmBillingAction(
      titleKey,
      messageKey,
      'billing.conflicts.changePlanAction',
    );
    if (!confirmed) {
      trackAuthEvent('checkout_conflict_resolved', {
        conflict_code: conflict.code,
        resolution: 'dismissed',
        surface: CHECKOUT_SURFACE,
      });
      setError(t('billing.checkout.actionRequired'));
      return true;
    }
    try {
      await billingAPI.changePlan(planTier, billingCycle);
      await billingAPI.refresh();
      await refreshAuth();
      await onConflictResolvedRef.current?.({ action: 'change_plan' });
      trackAuthEvent('checkout_conflict_resolved', {
        conflict_code: conflict.code,
        resolution: 'change_plan',
        surface: CHECKOUT_SURFACE,
      });
      onCloseRef.current();
    } catch (actionError: any) {
      const actionConflict = getBillingCheckoutConflict(actionError);
      if (actionConflict?.action === 'checkout') {
        return retryFreshCheckoutOnce(actionConflict.code);
      }
      const diagnostics = getCheckoutErrorDetails(actionError);
      trackAuthEvent('checkout_conflict_resolution_failed', {
        conflict_code: conflict.code,
        resolution: 'change_plan',
        error_code: diagnostics.errorCode,
        error_type: diagnostics.errorType,
        surface: CHECKOUT_SURFACE,
      });
      showInlineCheckoutFailure(actionError);
    }
    return true;
  }, [billingCycle, confirmBillingAction, planTier, refreshAuth, showInlineCheckoutFailure, t]);

  const fetchClientSecret = useCallback(async (options?: { manualRetry?: boolean }) => {
    const gate = gateRef.current;
    const attempt = gate.tryBegin(options);
    if (attempt === null) return;
    if (options?.manualRetry) setClientSecret(null);
    const retryCheckout = () => requestCheckoutSession(attempt);
    setLoading(true);
    setError(null);
    try {
      await retryCheckout();
    } catch (err: any) {
      // A stale or duplicate response must never replace a loaded Stripe
      // form (or the UI of a later open) with an error.
      if (!gate.isCurrent(attempt) || gate.hasMountedForm()) return;
      // Paywall-offer decline: the backend re-validated eligibility and said
      // no (already used, not eligible, or — final_offer only — a trial flag
      // slipped through). This is not a checkout failure — drop the promo
      // and retry once, silently, at full price.
      if (promo && !promoDeclinedRef.current) {
        const decline = getPaywallOfferDecline(err);
        if (decline) {
          promoDeclinedRef.current = true;
          if (promo === 'final_offer') {
            recordFinalOfferDeclined();
          } else {
            recordComebackOfferDeclined();
          }
          trackAuthEvent('paywall_offer_declined', {
            offer_type: promo === 'final_offer' ? 'final_offer' : 'comeback_discount',
            trigger: 'paywall_abandonment',
            offer_step: promo === 'final_offer' ? 2 : 1,
            reason: decline.code,
            surface: CHECKOUT_SURFACE,
          });
          try {
            await retryCheckout();
            return;
          } catch (retryErr: any) {
            err = retryErr;
          }
          if (!gate.isCurrent(attempt) || gate.hasMountedForm()) return;
        }
      }
      const properties = {
        plan: planTier,
        billing_period: billingCycle,
      };
      const feedback = reportCheckoutFailure({
        error: err,
        surface: CHECKOUT_SURFACE,
        checkoutKey: `${planTier}:${billingCycle}`,
        properties,
        t,
      });
      const handled = await handleCheckoutConflict(err, retryCheckout, feedback);
      if (!handled) {
        markCheckoutErrorShown(feedback, CHECKOUT_SURFACE, properties);
        gate.setMountedSecret(null);
        setClientSecret(null);
        setError(feedback.message);
      }
    } finally {
      if (gate.isCurrent(attempt)) {
        gate.finish(attempt);
        setLoading(false);
      }
    }
  }, [billingCycle, handleCheckoutConflict, planTier, promo, requestCheckoutSession, t]);

  const fetchClientSecretRef = useRef(fetchClientSecret);
  fetchClientSecretRef.current = fetchClientSecret;

  // Request once per open (and per plan/cycle/trial/promo), never because a
  // parent re-render changed some callback's identity.
  useEffect(() => {
    if (visible && isWeb) {
      if (gateRef.current.shouldRequestOnOpen(requestKey)) {
        setClientSecret(null);
        void fetchClientSecretRef.current();
      }
    } else {
      gateRef.current.close();
      completedRef.current = false;
      setClientSecret(null);
      setError(null);
      setLoading(false);
    }
  }, [visible, isWeb, requestKey]);

  const handleRetry = useCallback(() => {
    void fetchClientSecret({ manualRetry: true });
  }, [fetchClientSecret]);

  // Stable for the lifetime of the modal (Stripe reads onComplete once at
  // mount) and fires the parent's completion handler at most once per session.
  const handleComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    void onCompleteRef.current();
  }, []);

  const handleStripeLoadError = useCallback(() => {
    showInlineCheckoutFailure(createCheckoutError('stripe_js_load'));
  }, [showInlineCheckoutFailure]);

  if (!isWeb || !visible) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={styles.container} testID="embedded-checkout-modal">
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>
            {cardTrial
              ? t('billing.checkout.startTrialTitle', { days: String(trialPeriodDays) })
              : t('billing.checkout.completeTitle')}
          </Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <MaterialIcons name="close" size={24} color={colors.text.secondary} />
          </TouchableOpacity>
        </View>

        {/* Content */}
        <View style={styles.content}>
          {loading && (
            <View style={styles.loadingContainer} testID="embedded-checkout-preparing">
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={styles.loadingText}>{t('billing.checkout.preparing')}</Text>
            </View>
          )}

          {error && (
            <View style={styles.errorContainer}>
              <MaterialIcons name="error-outline" size={32} color={colors.error} />
              <Text style={styles.errorText}>{error}</Text>
              <TouchableOpacity
                onPress={handleRetry}
                disabled={loading || retryCooldown > 0}
                style={[styles.retryButton, (loading || retryCooldown > 0) && styles.retryButtonDisabled]}
              >
                <Text style={styles.retryText}>
                  {retryCooldown > 0
                    ? `${t('billing.checkout.retry')} (${retryCooldown})`
                    : t('billing.checkout.retry')}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {clientSecret && !loading && !error && (
            <StripeEmbeddedCheckout
              clientSecret={clientSecret}
              publishableKey={billingAPI._stripePublishableKey || ''}
              onComplete={handleComplete}
              onLoadError={handleStripeLoadError}
            />
          )}
        </View>
      </View>
    </Modal>
  );
}

/**
 * Inner component that renders the actual Stripe Embedded Checkout.
 * Dynamically imports @stripe/stripe-js and @stripe/react-stripe-js
 * so they're only loaded on web when needed.
 */
function StripeEmbeddedCheckout({
  clientSecret,
  publishableKey,
  onComplete,
  onLoadError,
}: {
  clientSecret: string;
  publishableKey: string;
  onComplete: () => void;
  onLoadError: () => void;
}) {
  const { t } = useTranslation();
  const [StripeComponents, setStripeComponents] = useState<any>(null);
  const [stripePromise, setStripePromise] = useState<any>(null);

  useEffect(() => {
    let active = true;
    setStripeComponents(null);
    setStripePromise(null);

    // Dynamic import to avoid bundling on native. Await loadStripe so a CSP or
    // network failure becomes a recoverable UI error instead of an unhandled
    // promise rejection.
    void (async () => {
      try {
        const [stripeJs, reactStripe] = await Promise.all([
          import('@stripe/stripe-js'),
          import('@stripe/react-stripe-js'),
        ]);
        const stripeInstance = await stripeJs.loadStripe(publishableKey);
        if (!stripeInstance) {
          throw new Error('Stripe.js returned no instance');
        }
        if (active) {
          setStripePromise(stripeInstance);
          setStripeComponents(reactStripe);
        }
      } catch (error) {
        // A tab opened before a deploy asks for chunk files the deploy removed:
        // reload onto the new build instead of showing a dead checkout.
        if (isStaleChunkError(error) && (await reloadIfNewerBuild())) return;
        if (active) onLoadError();
      }
    })();

    return () => {
      active = false;
    };
  }, [onLoadError, publishableKey]);

  if (!StripeComponents || !stripePromise) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>{t('billing.checkout.loadingPaymentForm')}</Text>
      </View>
    );
  }

  const { EmbeddedCheckoutProvider, EmbeddedCheckout } = StripeComponents;

  return (
    <EmbeddedCheckoutProvider
      stripe={stripePromise}
      options={{ clientSecret, onComplete }}
    >
      <EmbeddedCheckout />
    </EmbeddedCheckoutProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text.primary,
  },
  closeButton: {
    padding: spacing.xs,
  },
  content: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
  },
  loadingText: {
    fontSize: 16,
    color: colors.text.secondary,
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
    gap: spacing.md,
  },
  errorText: {
    fontSize: 16,
    color: colors.text.secondary,
    textAlign: 'center',
  },
  retryButton: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    backgroundColor: colors.primary,
    borderRadius: 8,
  },
  retryButtonDisabled: {
    opacity: 0.5,
  },
  retryText: {
    color: '#FFF',
    fontWeight: '600',
  },
});
